// File: src/components/admin/plans/PlanMarketingFields.js
// i18n follow-up: locale-aware marketing-copy editor.
//
// EN tab edits the canonical fields on the plan (displayName, tagline,
// featureBullets). Other locale tabs edit `draft.i18n[locale]`. Empty
// locale fields fall back to English at render time on the public pricing
// page (see backend applyLocaleToPlan).
//
// A "Translate with AI" button (enabled only on the English tab when the
// source has content) calls `POST /admin/plans/:id/translate` to fill every
// other locale in one go. The result hydrates `draft.i18n` so the operator
// sees the proposed copy and can tweak it before saving.

import React, { useState } from 'react';
import {
    Box, Stack, TextField, IconButton, Button, Typography, Tabs, Tab, Tooltip,
    Alert, CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { mainApi } from '../../../services/mainApi';

// Mirror SUPPORTED_LOCALES from the BE. Update in lockstep when adding a new
// supported language.
const LOCALES = [
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
];
const MAX_BULLETS = 8;

function emptyLocaleEntry() {
    return { displayName: '', tagline: '', featureBullets: [] };
}

/**
 * Read the editor view for the active locale.
 *   - For EN, reads canonical draft fields.
 *   - For others, reads draft.i18n[locale] (initialising to empty if missing).
 */
function readView(draft, locale) {
    if (locale === 'en') {
        return {
            displayName: draft.displayName || '',
            tagline: draft.tagline || '',
            featureBullets: Array.isArray(draft.featureBullets) ? draft.featureBullets : [],
        };
    }
    const i18n = draft.i18n || {};
    const entry = i18n[locale] || emptyLocaleEntry();
    return {
        displayName: entry.displayName || '',
        tagline: entry.tagline || '',
        featureBullets: Array.isArray(entry.featureBullets) ? entry.featureBullets : [],
    };
}

/**
 * Patch the active locale's view back into the draft. Returns a partial
 * update object suitable for the editor's `patch()`.
 */
function patchView(draft, locale, updates) {
    if (locale === 'en') return { ...updates };
    const i18n = { ...(draft.i18n || {}) };
    const current = i18n[locale] || emptyLocaleEntry();
    i18n[locale] = { ...current, ...updates };
    return { i18n };
}

export default function PlanMarketingFields({
    plan, draft, patch, activeLocale: controlledLocale, onLocaleChange,
}) {
    // Allow controlled (parent owns state) or uncontrolled (local) usage so
    // the preview card can mirror the active tab without a redundant prop.
    const [localLocale, setLocalLocale] = useState('en');
    const activeLocale = controlledLocale ?? localLocale;
    const setActiveLocale = onLocaleChange ?? setLocalLocale;
    const [translating, setTranslating] = useState(false);
    const [translateError, setTranslateError] = useState(null);
    const [translateSuccess, setTranslateSuccess] = useState(null);

    const view = readView(draft, activeLocale);
    const isEnglish = activeLocale === 'en';

    // English source must have content before the operator can ask the AI
    // to translate. Otherwise the AI translates blanks into blanks.
    const englishHasContent = Boolean(
        (draft.displayName || '').trim()
        || (draft.tagline || '').trim()
        || (Array.isArray(draft.featureBullets) && draft.featureBullets.some(b => (b || '').trim())),
    );

    function setField(key, value) {
        patch(patchView(draft, activeLocale, { [key]: value }));
    }
    function setBullet(i, val) {
        const copy = [...view.featureBullets];
        copy[i] = val;
        patch(patchView(draft, activeLocale, { featureBullets: copy }));
    }
    function addBullet() {
        if (view.featureBullets.length >= MAX_BULLETS) return;
        patch(patchView(draft, activeLocale, { featureBullets: [...view.featureBullets, ''] }));
    }
    function removeBullet(i) {
        const copy = view.featureBullets.filter((_, j) => j !== i);
        patch(patchView(draft, activeLocale, { featureBullets: copy }));
    }

    async function handleTranslate() {
        setTranslating(true);
        setTranslateError(null);
        setTranslateSuccess(null);
        try {
            const result = await mainApi.translatePlan(plan.id, {});
            // Hydrate the draft's i18n map with the AI-proposed translations.
            // The operator must still click Save to persist (so they can
            // tweak the AI output if needed).
            const newI18n = { ...(draft.i18n || {}) };
            for (const [loc, val] of Object.entries(result?.plan?.i18n || {})) {
                newI18n[loc] = val;
            }
            patch({ i18n: newI18n });

            const errCount = Object.keys(result?.errors || {}).length;
            const okCount = (result?.translated || []).length;
            if (errCount > 0) {
                setTranslateError(`Translated ${okCount} locale(s); ${errCount} failed: ${JSON.stringify(result.errors)}`);
            } else {
                setTranslateSuccess(`Translated to ${result.translated.join(', ')}. Review on each tab and click Save to publish.`);
            }
        } catch (e) {
            setTranslateError(e?.response?.data?.error || e.message);
        } finally {
            setTranslating(false);
        }
    }

    const hasOverrides = activeLocale !== 'en'
        && Boolean(draft.i18n?.[activeLocale]
            && (view.displayName || view.tagline || view.featureBullets.some(b => b)));

    return (
        <Box data-testid={`plan-marketing-${plan.id}`}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Tabs
                    value={activeLocale}
                    onChange={(_e, v) => setActiveLocale(v)}
                    sx={{ flex: 1, minHeight: 36 }}
                    data-testid={`plan-locale-tabs-${plan.id}`}
                >
                    {LOCALES.map(l => (
                        <Tab
                            key={l.code}
                            value={l.code}
                            label={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <span>{l.label}</span>
                                    {l.code !== 'en' && draft.i18n?.[l.code] && (
                                        <Chip size="small" label="✓" sx={{ height: 16, fontSize: 10 }} />
                                    )}
                                </Stack>
                            }
                            data-testid={`plan-locale-tab-${plan.id}-${l.code}`}
                            sx={{ minHeight: 36, textTransform: 'none' }}
                        />
                    ))}
                </Tabs>
                <Tooltip title={isEnglish && englishHasContent
                    ? 'Use EdenAI to translate the English copy into every other supported locale.'
                    : 'Switch to the English tab and fill in the source copy first.'}>
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={translating ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                            onClick={handleTranslate}
                            disabled={!isEnglish || !englishHasContent || translating}
                            data-testid={`plan-translate-btn-${plan.id}`}
                        >
                            Translate with AI
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            {translateError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setTranslateError(null)}
                    data-testid={`plan-translate-error-${plan.id}`}>
                    {translateError}
                </Alert>
            )}
            {translateSuccess && (
                <Alert severity="success" sx={{ mb: 1 }} onClose={() => setTranslateSuccess(null)}
                    data-testid={`plan-translate-success-${plan.id}`}>
                    {translateSuccess}
                </Alert>
            )}

            {!isEnglish && !hasOverrides && (
                <Alert severity="info" sx={{ mb: 1 }} data-testid={`plan-locale-fallback-${plan.id}-${activeLocale}`}>
                    No {activeLocale.toUpperCase()} override yet. Customer pricing page will show the English copy for this locale until you fill these fields (or click "Translate with AI").
                </Alert>
            )}

            <Stack spacing={2}>
                <TextField
                    label={`Display name (${activeLocale.toUpperCase()})`}
                    size="small"
                    fullWidth
                    value={view.displayName}
                    onChange={(e) => setField('displayName', e.target.value)}
                    inputProps={{ 'data-testid': `plan-displayName-${plan.id}-${activeLocale}` }}
                />
                <TextField
                    label={`Tagline (${activeLocale.toUpperCase()})`}
                    size="small"
                    fullWidth
                    value={view.tagline}
                    onChange={(e) => setField('tagline', e.target.value)}
                    inputProps={{ 'data-testid': `plan-tagline-${plan.id}-${activeLocale}` }}
                />
                <Box>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            Feature bullets ({view.featureBullets.length}/{MAX_BULLETS})
                        </Typography>
                        <Tooltip title="Add bullet">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={addBullet}
                                    disabled={view.featureBullets.length >= MAX_BULLETS}
                                    data-testid={`plan-bullet-add-${plan.id}-${activeLocale}`}
                                >
                                    <AddIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        {view.featureBullets.map((b, i) => (
                            <Stack key={i} direction="row" spacing={1} alignItems="center">
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={b}
                                    onChange={(e) => setBullet(i, e.target.value)}
                                    inputProps={{ 'data-testid': `plan-bullet-${plan.id}-${activeLocale}-${i}` }}
                                />
                                <IconButton size="small" onClick={() => removeBullet(i)}
                                    data-testid={`plan-bullet-remove-${plan.id}-${activeLocale}-${i}`}>
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
}
